import React, { useState } from 'react';
import { motion, useDragControls } from 'framer-motion';
import { Tag, Plus, X, Bookmark, BookmarkCheck, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

const KeywordsPanel = ({ 
  keywords, 
  savedKeywords, 
  onSaveKeyword, 
  onDeleteKeyword,
  audience,
  onClose
}) => {
  const [showSaved, setShowSaved] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const dragControls = useDragControls();

  // Check if a keyword is already saved
  const isKeywordSaved = (keyword) => {
    return savedKeywords.some(sk => sk.keyword.toLowerCase() === keyword.toLowerCase());
  };

  // Filter saved keywords for current audience
  const filteredSavedKeywords = savedKeywords.filter(
    sk => !audience || sk.audience === audience || sk.audience === 'general'
  );

  return (
    <motion.div
      className="fixed z-[60] w-72 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-xl overflow-hidden"
      style={{ top: 140, right: 24 }}
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      drag
      dragControls={dragControls}
      dragMomentum={false}
      dragElastic={0}
      data-testid="keywords-panel"
    >
      {/* Header - Draggable */}
      <div 
        className="bg-gradient-to-r from-[#0070FF] to-[#FF7F00] p-3 cursor-move"
        onPointerDown={(e) => dragControls.start(e)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-white/70" />
            <Tag className="w-4 h-4 text-white" />
            <h3 className="font-bold text-sm text-white">KEYWORDS</h3>
          </div>
          <div className="flex items-center gap-1">
            {/* Minimize Button */}
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title={isMinimized ? "Expand" : "Minimize"}
              data-testid="keywords-minimize-btn"
            >
              {isMinimized ? (
                <ChevronDown className="w-4 h-4 text-white" />
              ) : (
                <ChevronUp className="w-4 h-4 text-white" />
              )}
            </button>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Close panel"
              data-testid="keywords-close-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
        
        {/* Tabs - Only show when not minimized */}
        {!isMinimized && (
          <div className="flex gap-1 mt-2">
            <button
              onClick={() => setShowSaved(false)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                !showSaved ? 'bg-white text-[#0070FF]' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Suggested ({keywords.length})
            </button>
            <button
              onClick={() => setShowSaved(true)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                showSaved ? 'bg-white text-[#0070FF]' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Saved ({filteredSavedKeywords.length})
            </button>
          </div>
        )}
      </div>

      {/* Content - Collapsible */}
      {!isMinimized && (
        <>
          <ScrollArea className="h-[250px]">
            <div className="p-4">
              {!showSaved ? (
                /* Suggested Keywords */
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-3">Click to save keywords for future use</p>
                  <div className="flex flex-wrap gap-2">
                    {keywords.map((keyword, index) => {
                      const saved = isKeywordSaved(keyword);
                      return (
                        <motion.button
                          key={index}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all ${
                            saved 
                              ? 'bg-[#0070FF]/10 text-[#0070FF] border border-[#0070FF]/30'
                              : 'bg-gray-100 hover:bg-[#0070FF]/10 hover:border-[#0070FF]/30 border border-transparent'
                          }`}
                          onClick={() => !saved && onSaveKeyword(keyword)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          data-testid={`keyword-${index}`}
                        >
                          {saved ? (
                            <BookmarkCheck className="w-3 h-3 text-green-600" />
                          ) : (
                            <Plus className="w-3 h-3 text-gray-400" />
                          )}
                          <span>{keyword}</span>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* Saved Keywords */
                <div className="space-y-2">
                  {filteredSavedKeywords.length === 0 ? (
                    <div className="text-center py-8">
                      <Bookmark className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No saved keywords yet</p>
                      <p className="text-xs text-gray-400 mt-1">Click on suggested keywords to save them</p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {filteredSavedKeywords.map((kw) => (
                        <motion.div
                          key={kw.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-[#0070FF] text-white"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          <span>{kw.keyword}</span>
                          <button
                            onClick={() => onDeleteKeyword(kw.id)}
                            className="ml-1 p-0.5 hover:bg-white/20 rounded-full transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              {!showSaved 
                ? "Keywords improve content relevance & SEO"
                : "Saved keywords included in content generation"
              }
            </p>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default KeywordsPanel;
